/**
 * @author        nico@zumper.com (Nico Rattazzi)
 * @copyright     Copyright (c) 2017, Zumper
 * @description   helper functions into determining a value in state.
 */

import { getUser, selectUtmMedium } from '@zcode-ui/web-zumper-global-state'
import { TenantFeature } from '@zcode/enums'
import {
  createSelector,
  memoizeMaybeRouteProps,
  memoizeRouteProps,
  memoizeStateByRouteProps,
} from '@zcode/memoizers'
import { PAGE_NAMES } from '@zcode/mixpanel'
import {
  BATHROOMS_RE,
  BEDROOMS_RE,
  FILTERS_START_RE,
  LUXURY_RE,
  OVER_RE,
  PRICE_RANGE_RE,
  STUDIO_RE,
  UNDER_RE,
  ZOOM_CLUSTER_FILTER_NOT_APPLICABLE,
  getVerticalsArray as _getVerticalsArray,
  oisEmpty,
} from '@zcode/nr-utils'
import { hasHasGalleryParam } from '@zcode/nr/es/shared/bizos/rentable/selectors'
import { toSlug } from '@zcode/nr/es/shared/components/Autocomplete/helpers/toSlug'
import { chomp } from '@zcode/nr/es/shared/utils'
import * as LocationUtils from '@zcode/nr/es/shared/utils/location'
import uniqueListables from '@zcode/nr/es/shared/utils/uniqueListables'
import { matchPath } from '@zcode/router-utils'
import { isNumber } from '@zcode/utils'
import {
  currentSearchRoutes,
  detailRoutes,
  maplistRoutes,
  permanentAddressRoutes,
  rentResearchRoutes,
  routesConstants,
} from '@zcode/zumper-routes'
import qs from 'querystringify'
import { compose } from 'redux'
import * as ActionC from '../actions/constants'
import { default as matchLocationPath } from '../components/main/Rail/CurrentSearchView/helpers/matchLocationPath'
import { selectIsNavbarExpanded } from '../modules/currentSearch/selectors/index'
import { selectEntity } from '../modules/detail/modules/entity/selectors/index'
import { selectIsLoadingListables as selectIsLoadingFavoritesListables } from '../modules/favoritesView/selectors/index'
import { selectIsFilterModalOpen } from '../modules/filters/selectors/filterModal'
import {
  selectFlaggedBuildings,
  selectFlaggedListings,
} from '../modules/flaggedIds/selectors/index'
import { selectIsLoadingListables as selectIsLoadingMessagedListables } from '../modules/messagedView/selectors/index'
import { selectOpen as selectAnyModalOpen } from '../modules/modals/selectors/index'
import { selectIsLoadingListables as selectIsLoadingSavedSearchesListables } from '../modules/savedSearchesView/selectors/index'
import { PROPERTY_TYPE_PATH_RE } from '../utils/urls/constants'

/*
 * State Inspectors
 */

///
// Copied from packages/nzr/src/modules/router/selectors/index.js
// To remove circular dep
/**
 *
 * @param {import('react-router-dom').RouteChildrenProps} routeProps
 * @returns
 */
export const selectLocation = (routeProps) => routeProps?.location
///
/**
 *
 * @param {import('@zcode/state-type').State} state
 * @param {import('@zcode/state-type').Listable | import('@zcode/state-type').Pin<import('@zcode/state-type').RawPinInventorySearch> | import('@zcode/state-type').Property | null | undefined} [givenEntity]
 */
export const isEntityFavorited = (state, givenEntity) => {
  const entity = givenEntity || selectEntity(state)
  const { buildingId, listingId } = entity || {}
  const { buildings, listings } = state.bundle.favorites

  if (!buildingId && !listingId) {
    return false
  }
  return buildingId ? !!buildings[buildingId.toString()] : !!listings[listingId.toString()]
}

/**
 *
 * @param {import('@zcode/state-type').State} state
 * @param {import('@zcode/state-type').Property} entity
 */
export const isEntityFlagged = (state, entity) => {
  const { buildingId, listingId } = entity
  const flaggedBuildings = selectFlaggedBuildings(state)
  const flaggedListings = selectFlaggedListings(state)

  if (!buildingId && !listingId) {
    return false
  }

  return buildingId ? !!flaggedBuildings[buildingId] : !!flaggedListings[listingId]
}

export const getCurrentLocationCoords = createSelector(
  (state) => state.currentSearch.location.lat,
  (state) => state.currentSearch.location.lng,
  (lat, lng) => ({ lat, lng })
)

/**
 *
 * @param {import('@zcode/state-type').State} state
 * @param {import('@zcode/state-type').Listable | import('@zcode/state-type').Property | import('@zcode/state-type').MCity} [givenEntity]
 * @returns {number | null}
 */
export const getDistance = (state, givenEntity) => {
  const entity = givenEntity || selectEntity(state)
  const center = getCurrentLocationCoords(state)
  const distance = LocationUtils.getDistanceFromCenter(center, entity)
  const precision = distance >= 10 ? 0 : 1
  return chomp(distance, precision)
}

/**
 *
 * @param {import('@zcode/state-type').State} state
 * @param {import('@zcode/state-type').Listable | import('@zcode/state-type').Property} [givenEntity]
 */
export const getFavoritedTime = (state, givenEntity) => {
  const entity = givenEntity || selectEntity(state)
  const { buildingId, listingId } = entity || {}
  const { buildings, listings } = state.bundle.favorites

  if (!buildingId && !listingId) {
    return false
  }
  return buildingId ? buildings[buildingId.toString()] : listings[listingId.toString()]
}

export const getMessaged = (/** @type {import('@zcode/state-type').State} */ state) =>
  state.bundle && state.bundle.messaged

/**
 *
 * @param {import('@zcode/state-type').State} state
 * @returns
 */
export const getMessagedSize = (state) => {
  if (!state.bundle || !state.bundle.messaged) {
    return 0
  }
  const { messaged } = state.bundle
  return Object.keys(messaged.buildings).length + Object.keys(messaged.listings).length
}

/**
 *
 * @param {ReturnType<typeof getMessaged>} messaged
 * @param {Pick<
 *   import('@zcode/state-type').Listable | import('@zcode/state-type').MaybeEmptyProperty | import('@zcode/state-type').Pin<import('@zcode/state-type').RawPinInventorySearch>,
 *   'pbId' | 'buildingId' | 'plId' | 'listingId'
 * > | null | undefined} entity
 * @returns {number | null}
 */
const getEntityMessageSentTime = (messaged, entity) => {
  if (!entity) {
    return null
  }
  const { pbId, buildingId, plId, listingId } = entity
  const { buildings, listings } = messaged
  return (
    (pbId && buildings?.[pbId.toString()]) ||
    (buildingId && buildings?.[buildingId.toString()]) ||
    (plId && listings?.[plId.toString()]) ||
    (listingId && listings?.[listingId.toString()]) ||
    null
  )
}

/**
 *
 * @type {(
 *   state: import('@zcode/state-type').State,
 *   givenEntity?: import('@zcode/state-type').Listable | import('@zcode/state-type').Pin<import('@zcode/state-type').RawPinInventorySearch> | import('@zcode/state-type').MaybeEmptyProperty | null
 * ) => number | null}
 */
export const getMessageSentTime = createSelector(
  getMessaged,
  /**
   *
   * @param {import('@zcode/state-type').State} state
   * @param {import('@zcode/state-type').Listable | import('@zcode/state-type').MaybeEmptyProperty | null} [givenEntity]
   */
  (state, givenEntity) => {
    return givenEntity || selectEntity(state)
  },
  getEntityMessageSentTime
)

/**
 *
 * @param {ReturnType<typeof getMessaged>} messaged
 * @param {Pick<
 *   import('@zcode/state-type').Listable | import('@zcode/state-type').MaybeEmptyProperty,
 *   'pbId' | 'buildingId' | 'plId' | 'listingId'
 * > | null | undefined} entity
 */
export const getEntityIsMessaged = (messaged, entity) => {
  return !!getEntityMessageSentTime(messaged, entity)
}

/**
 *
 * @param {import('@zcode/state-type').State} state
 * @param {import('@zcode/state-type').Listable | import('@zcode/state-type').MaybeEmptyProperty | import('@zcode/state-type').Pin<import('@zcode/state-type').RawPinInventorySearch> | null} [givenEntity]
 */
export const isMessaged = (state, givenEntity) => {
  return !!getMessageSentTime(state, givenEntity)
}

/**
 * For EQUAL_CTA_ALL experiment only
 *
 * @param {import('@zcode/state-type').State} state
 * @param {import('@zcode/state-type').Listable | import('@zcode/state-type').Pin<import('@zcode/state-type').RawPinInventorySearch> | import('@zcode/state-type').Property | null} [givenEntity]
 */
export const isEqualTourMessaged = (state, givenEntity) => {
  const entity = givenEntity || selectEntity(state)
  if (!entity) {
    return false
  }
  const { pbId, buildingId, listingId } = entity
  const { buildings, listings } = state?.bundle?.equalTourMessaged
  return !!(
    (pbId && buildings?.[pbId?.toString()]) ||
    (buildingId && buildings?.[buildingId?.toString()]) ||
    (listingId && listings?.[listingId?.toString()])
  )
}

/**
 *
 * @param {import('@zcode/state-type').State} state
 * @param {import('@zcode/state-type').Listable | import('@zcode/state-type').Property} [givenEntity]
 */
export const isEqualCheckAvailabilityMessaged = (state, givenEntity) => {
  const entity = givenEntity || selectEntity(state)
  if (!entity) {
    return false
  }
  const { pbId, buildingId, listingId } = entity
  const { buildings, listings } = state?.bundle?.equalCheckAvailabilityMessaged
  return !!(
    (pbId && buildings?.[pbId?.toString()]) ||
    (buildingId && buildings?.[buildingId?.toString()]) ||
    (listingId && listings?.[listingId?.toString()])
  )
}

/**
 *
 * @param {import('@zcode/state-type').State} state
 */
export const checkIsMessaged = (state) => isMessaged.bind(null, state)

/**
 *
 * @param {import('@zcode/state-type').State} state
 */
export const checkIsFavorited = (state) => isEntityFavorited.bind(null, state)

/**
 * The M3_ACTIVE feature is added to a user when they send a message which msgd determines
 * should be delivered via Messaging 3.0
 *
 * @param {import('@zcode/state-type').State} state
 * @returns {boolean}
 */
export const selectIsMessaging3Active = (state) => {
  return !!state.bundle?.user?.tenantFeatures?.includes(TenantFeature.M3_ACTIVE)
}

/**
 * @param {import('@zcode/state-type').State} state
 */
export const getAlertCount = (state) => {
  return state.bundle.alerts
}

export const getFavorited = (/** @type {import('@zcode/state-type').State} */ state) => {
  return state.bundle
    ? state.bundle.favorites
    : /** @type {import('@zcode/utility-types').Empty<import('@zcode/bizos').BuildingListingCollection>} */ ({})
}

/**
 * @param {import('@zcode/state-type').State} state
 * @returns {import('@zcode/state-type').Location['zoom']}
 */
export function getZoom(state) {
  // TODO: try to get the zoom from the box if zoom is empty
  return state.currentSearch.location.zoom
}

/**
 * @param {import('@zcode/state-type').State} state
 */
export const getFavoritedSize = (state) => {
  if (!state.bundle || !state.bundle.favorites) {
    return 0
  }
  const { favorites } = state.bundle
  return Object.keys(favorites.buildings).length + Object.keys(favorites.listings).length
}

/**
 * @param {import('@zcode/state-type').State} state
 */
export const getGeo = (state) => {
  return state.geo
}

export const selectNearbyCities = compose((geos) => geos && geos.nearbyCities, getGeo)

export const selectNearbyCitiesByCount = createSelector(
  selectNearbyCities,
  (nearbyCities) =>
    nearbyCities &&
    nearbyCities.filter((city) => city.listingCount).sort((a, b) => b.listingCount - a.listingCount)
)

export const getNearbyHoodIds = createSelector(
  /**
   * @param {import('@zcode/state-type').State} state
   */
  (state) => getCurrentCity(state),
  /**
   * @param {import('@zcode/state-type').State} state
   */
  (state) => state.geo && state.geo.nearbyHoods,
  (city, nearbyHoods) => {
    const nearbyHoodIds = {}
    if (city && city.neighborhoods && city.neighborhoods.length) {
      city.neighborhoods.forEach(({ id, listingCount }) => {
        nearbyHoodIds[id] = true
      })
    } else if (nearbyHoods && nearbyHoods.length) {
      nearbyHoods.forEach(({ id, listingCount }) => {
        nearbyHoodIds[id] = true
      })
    }

    return oisEmpty(nearbyHoodIds) ? undefined : nearbyHoodIds
  }
)

/**
 *
 * @param {import('@zcode/state-type').State} state
 * @returns
 */
export const getLocalUser = (state) => {
  return state.localUser
}

/**
 *
 * @param {import('@zcode/state-type').State} state
 * @returns
 */
export const getUserName = (state) => {
  const user = getUser(state)
  return user ? user.fullName : ''
}

/**
 *
 * @param {import('@zcode/state-type').State} state
 * @returns
 */
export const getUserEmail = (state) => {
  const user = getUser(state)
  return user ? user.email : ''
}

/**
 *
 * @param {import('@zcode/state-type').State} state
 * @returns
 */
export const getUserPhone = (state) => {
  const user = getUser(state)
  return user ? user.phone : ''
}

/**
 *
 * @param {import('@zcode/state-type').State} state
 * @returns
 */
export const getUserMoveIn = (state) => {
  const user = getLocalUser(state)
  return (user && user.tenantProfile && user.tenantProfile.moveIn) || ''
}

/**
 * @param {import('@zcode/state-type').State} state
 */
export const getCurrentSearch = (state) => {
  return state.currentSearch
}

/**
 * @param {import('@zcode/state-type').State} state
 */
export const getCurrentHood = (state) => {
  return state.currentSearch && state.currentSearch.hood
}

/**
 * @param {import('@zcode/state-type').State} state
 * @returns {import('@zcode/state-type').CurrentSearch['minCities']['minCities']}
 */
export const getCurrentMinCities = (state) => {
  return state.currentSearch.minCities.minCities
}

/**
 * @param {import('@zcode/state-type').State} state
 * @returns {import('@zcode/state-type').CurrentSearch['minCities']['minCities'][number] | undefined}
 */
export function getCurrentMinCity(state) {
  const cities = getCurrentMinCities(state)
  return cities && cities[0]
}

/**
 * @param {import('@zcode/state-type').State} state
 */
export function getCurrentCity(state) {
  return state.currentSearch && (state.currentSearch.city || getCurrentMinCity(state))
}

export const getNearbyCities = createSelector(
  getCurrentMinCities,
  getCurrentCity,
  getZoom,
  (minCities, currentCity, zoom) => {
    if (minCities && currentCity && zoom > ZOOM_CLUSTER_FILTER_NOT_APPLICABLE) {
      return minCities.filter((city) => city.cityId !== currentCity.cityId)
    }
    return minCities
  }
)

/**
 * @param {import('@zcode/state-type').State} state
 */
export const getHasNearbyCities = (state) => {
  const cities = getNearbyCities(state)
  return !!cities && !!cities.length
}

/**
 * @param {import('@zcode/state-type').State} state
 */
export const isFiltersViewState = (state) => {
  return state.searchUI && state.searchUI.isFiltersView
}

/**
 * @param {import('@zcode/state-type').State} state
 */
export const getFiltersViewStartPoint = (state) => {
  return state.searchUI && state.searchUI.filtersViewStartPoint
}

/**
 * @param {import('@zcode/state-type').State} state
 */
export const getFilters = (state) => {
  return state.filters && state.filters.filters
}

export const checkListablesFetching = memoizeStateByRouteProps(
  (
    /** @type {import('@zcode/state-type').State} */ state,
    /** @type {import('@zcode/memoizers').MaybeNoMatchRouteProps} */ routeProps
  ) => {
    const isFavoritesView = checkFavoritesView(routeProps)
    const isMessagedView = checkMessagedView(routeProps)
    const isSavedSearchesView = checkSavedSearchesView(routeProps)

    if (isFavoritesView) {
      return selectIsLoadingFavoritesListables(state)
    }
    if (isMessagedView) {
      return selectIsLoadingMessagedListables(state)
    }
    if (isSavedSearchesView) {
      return selectIsLoadingSavedSearchesListables(state)
    }
  }
)

// Determines whether current location is valid
/**
 * @param {import('@zcode/state-type').State} state
 */
export function hasValidLocation(state) {
  const { location } = state.currentSearch
  const validBox = location.box && Object.values(location.box).every(isNumber)
  const validCoordinate = isNumber(location.lat) && isNumber(location.lng)

  return !!(location && (location.url || validBox || validCoordinate))
}

// Determines whether homepage shows or not
/**
 * @param {import('@zcode/state-type').State} state
 */
export function isBoxSearch(state) {
  return LocationUtils.isBoxSearch(state.currentSearch.location)
}

/**
 * @param {import('@zcode/state-type').State} state
 */
export function getSearchView(state) {
  return state.searchUI && state.searchUI.searchView
}

/**
 * @param {import('@zcode/state-type').State} state
 */
export function isNavbarSearchFocused(state) {
  return state.searchUI && state.searchUI.isNavbarSearchFocused
}

export const checkMainView = memoizeRouteProps((routeProps) => {
  const { location } = routeProps
  if (!location) {
    return false
  }
  const { pathname } = location

  return (
    !pathname.startsWith(routesConstants.NZR_FAVORITES) &&
    !pathname.startsWith(routesConstants.NZR_ALERTS) &&
    !pathname.startsWith(routesConstants.NZR_MESSAGED)
  )
})

export const checkDetailView = memoizeMaybeRouteProps((routeProps) => {
  const { location } = routeProps
  return !!(
    location &&
    location.pathname &&
    detailRoutes.some((route) => matchPath(location.pathname, route))
  )
})

export const checkGalleryView = memoizeMaybeRouteProps((routeProps) => {
  const { location } = routeProps
  return !!(location && location.search && hasHasGalleryParam(location.search))
})

export const checkPermanentAddressView = memoizeMaybeRouteProps((routeProps) => {
  const { location } = routeProps
  return !!(
    location &&
    location.pathname &&
    permanentAddressRoutes.some((route) => matchPath(location.pathname, route))
  )
})

export const checkRentResearchView = memoizeMaybeRouteProps((routeProps) => {
  const { location } = routeProps
  return !!(
    location &&
    location.pathname &&
    rentResearchRoutes.some((route) => matchPath(location.pathname, route))
  )
})

export const selectIsRailPage = memoizeMaybeRouteProps((routeProps) => {
  const { location } = routeProps
  return !!(
    location &&
    location.pathname &&
    (maplistRoutes.some((route) => matchPath(location.pathname, route)) ||
      maplistRoutes.some((route) => matchPath(location.pathname, route))) &&
    !checkDetailView(routeProps)
  )
})

export const selectIsCurrentSearchView = memoizeRouteProps((routeProps) => {
  const { location } = routeProps
  return !!(
    location &&
    location.pathname &&
    currentSearchRoutes.some((route) => matchPath(location.pathname, route)) &&
    !checkDetailView(routeProps)
  )
})

export const checkFavoritesView = memoizeMaybeRouteProps(
  (/** @type {import('@zcode/memoizers').MaybeNoMatchRouteProps} */ routeProps) => {
    const { location } = routeProps
    return location && location.pathname
      ? location.pathname.startsWith(routesConstants.NZR_FAVORITES)
      : false
  }
)

export const isUtmMediumEmail = memoizeRouteProps((routeProps) => {
  const utmMedium = selectUtmMedium(routeProps)
  return utmMedium === 'email'
})

export const checkMessagedView = memoizeRouteProps((routeProps) => {
  const { location } = routeProps
  return location && location.pathname
    ? location.pathname.startsWith(routesConstants.NZR_MESSAGED)
    : false
})

// Determine if search view is either favorites or messaged
export const checkInterestedView = memoizeRouteProps((routeProps) => {
  return (
    checkSavedSearchesView(routeProps) ||
    checkMessagedView(routeProps) ||
    checkFavoritesView(routeProps)
  )
})

/**
 * Determines whether the server couldn't help us locate our location
 *
 * @param {import('@zcode/state-type').State} state
 */
export function checkMissingLocation(state) {
  return !!state.currentSearch.location.missing
}

/**
 * @param {import('@zcode/state-type').State} state
 */
export const checkIfMoreResult = (state) => {
  return state.searchUI && state.searchUI.moreResult
}

export const checkSavedSearchesView = memoizeRouteProps((routeProps) => {
  const { location } = routeProps
  return !!(
    location &&
    location.pathname &&
    location.pathname.startsWith(routesConstants.NZR_ALERTS)
  )
})

export const checkHomepageView = memoizeMaybeRouteProps((routeProps) => {
  const { location } = routeProps
  return !!(location && location.pathname === routesConstants.NZR_HOMEPAGE)
})

export const selectPageName = memoizeRouteProps((routeProps) => {
  if (checkHomepageView(routeProps)) {
    return PAGE_NAMES.HOMEPAGE
  } else if (checkDetailView(routeProps)) {
    return PAGE_NAMES.DETAIL
  } else if (selectIsCurrentSearchView(routeProps)) {
    return PAGE_NAMES.SEARCH
  } else if (checkPermanentAddressView(routeProps)) {
    return PAGE_NAMES.PERM_ADDRESS
  }
})

export const getPathname = memoizeRouteProps(
  /**
   *
   */
  /**
   *
   */
  /**
   *
   */
  /**
   *
   */
  /**
   *
   */
  /**
   * @param routeProps
   */
  (routeProps) => {
    const { location } = routeProps
    return location && location.pathname
  }
)

/**
 * @param {import('@zcode/state-type').State} state
 */
export function checkSaveSearchCreateView(state) {
  return state.searchUI && state.searchUI.searchView === ActionC.SEARCH_VIEW_TYPE.SAVE_SEARCH_CREATE
}

/**
 * @param {import('@zcode/state-type').State} state
 */
export function getListablesRecordByView(state) {
  const searchView = state.searchUI ? state.searchUI.searchView : ActionC.SEARCH_VIEW_TYPE.MAIN

  switch (searchView) {
    case ActionC.SEARCH_VIEW_TYPE.SEARCHES: {
      return state.savedSearchesView.listables
    }
    case ActionC.SEARCH_VIEW_TYPE.MAIN:
    case ActionC.SEARCH_VIEW_TYPE.SAVE_SEARCH_CREATE: {
      return state.currentSearch.listables
    }
    default:
      return null
  }
}

export const getCurrentSearchLimit = (/** @type {import('@zcode/state-type').State} */ state) => {
  return state.searchUI.limit
}
export const getCurrentSearchOffset = (/** @type {import('@zcode/state-type').State} */ state) => {
  return state.searchUI.offset
}
export const getCurrentSearchPageOffset = (
  /** @type {import('@zcode/state-type').State} */ state
) => {
  return state.searchUI.pageOffset
}

// CurrentSearch Listables
export const getCurrentSearchListablesQuery = (
  /** @type {import('@zcode/state-type').State} */ state
) => {
  return state.currentSearch.listables.query
}
export const getHasCurrentSearchListables = (
  /** @type {import('@zcode/state-type').State} */ state
) => {
  const featured = state.currentSearch.listables.featured
  const listables = state.currentSearch.listables.listables
  return !!((featured && featured.length) || (listables && listables.length))
}
export const getCurrentSearchListables = createSelector(
  /**
   *
   * @param {import('@zcode/state-type').State} state
   * @returns {import('@zcode/state-type').Listables['spotlight']}
   */
  (state) => state.currentSearch.listables.spotlight,
  /**
   *
   * @param {import('@zcode/state-type').State} state
   * @returns {import('@zcode/state-type').Listables['featured']}
   */
  (state) => state.currentSearch.listables.featured,
  /**
   *
   * @param {import('@zcode/state-type').State} state
   * @returns {import('@zcode/state-type').Listables['listables']}
   */
  (state) => state.currentSearch.listables.listables,
  (spotlight, featured, listables) => {
    const spotAndFeat = spotlight?.length ? spotlight.concat(featured) : featured
    return uniqueListables(spotAndFeat?.length ? spotAndFeat.concat(listables) : listables)
  }
)

export const selectFirstListableTimezone = compose(
  (listables) => listables[0]?.tz ?? null,
  getCurrentSearchListables
)

/**
 * @param {import('@zcode/state-type').State} state
 * @returns boolean
 */
export const getCurrentSearchIsLowResults = (state) => {
  return !!state.currentSearch.listables.isLowResults
}
/**
 * @param {import('@zcode/state-type').State} state
 */
export const getCurrentSearchMatching = (state) => {
  return state.currentSearch.listables.matching
}
/**
 * @param {import('@zcode/state-type').State} state
 * @returns {import('@zcode/state-type').Listables['maxBox']}
 */
export const getCurrentSearchMaxBox = (state) => {
  return state.currentSearch.listables.maxBox
}
/**
 * @param {import('@zcode/state-type').State} state
 */
export const getHasMoreCurrentSearchListables = (state) => {
  const matching = getCurrentSearchMatching(state)
  const listables = state.currentSearch.listables.listables
  const featured = state.currentSearch.listables.featured
  const count = listables.length + (featured?.length ?? 0)
  const offset = getCurrentSearchPageOffset(state)
  return matching > count + offset
}

// Nearby listables
/**
 * @param {import('@zcode/state-type').State} state
 */
export const getHasNearbyMatches = (state) => {
  const listables = state.currentSearch.listables.nearby?.matches?.listables
  return !!(listables && listables.length)
}
/**
 * @param {import('@zcode/state-type').State} state
 */
export const getHasNearbyPopular = (state) => {
  const listables = state.currentSearch.listables.nearby?.popular?.listables
  return !!(listables && listables.length)
}
/**
 * @param {import('@zcode/state-type').State} state
 */
export const getHasNearbyListables = (state) => {
  const hasMatches = getHasNearbyMatches(state)
  const hasPopular = getHasNearbyPopular(state)
  return hasMatches || hasPopular
}
/**
 * @param {import('@zcode/state-type').State} state
 * @returns {undefined | NonNullable<NonNullable<import('@zcode/state-type').State['currentSearch']['listables']['nearby']>['popular']>['box']}
 */
export const getNearbyBox = (state) => {
  const hasMatches = getHasNearbyMatches(state)
  const hasPopular = getHasNearbyPopular(state)
  let key
  if (hasMatches) {
    key = 'matches'
  } else if (hasPopular) {
    key = 'popular'
  } else {
    return undefined
  }
  return state.currentSearch.listables.nearby?.[key]?.box
}

/**
 * @type {(state: import('@zcode/state-type').State) => (undefined | NonNullable<NonNullable<import('@zcode/state-type').State['currentSearch']['listables']['nearby']>['matches']>['listables'])}
 */
export const getNearbyListables = createSelector(
  /**
   * @param {import('@zcode/state-type').State} state
   * @returns {undefined | NonNullable<NonNullable<import('@zcode/state-type').State['currentSearch']['listables']['nearby']>['matches']>['listables']}
   */
  (state) => {
    const hasMatches = getHasNearbyMatches(state)
    const hasPopular = getHasNearbyPopular(state)
    let key
    if (hasMatches) {
      key = 'matches'
    } else if (hasPopular) {
      key = 'popular'
    } else {
      return undefined
    }
    return state.currentSearch.listables.nearby?.[key]?.listables
  },
  (listables) => uniqueListables(listables)
)

export const getNearbyOnly = createSelector(
  /**
   * @param {import('@zcode/state-type').State} state
   * @returns {undefined | NonNullable<NonNullable<import('@zcode/state-type').State['currentSearch']['listables']['nearby']>['matches']>['listables']}
   */
  (state) => {
    return state.currentSearch.listables.nearby?.matches?.listables
  },
  (listables) => uniqueListables(listables)
)

export const getPopularOnly = createSelector(
  /**
   * @param {import('@zcode/state-type').State} state
   * @returns {undefined | NonNullable<NonNullable<import('@zcode/state-type').State['currentSearch']['listables']['nearby']>['popular']>['listables']}
   */
  (state) => {
    return state.currentSearch.listables.nearby?.popular?.listables
  },
  (listables) => uniqueListables(listables)
)

/**
 * @param {import('@zcode/state-type').State} state
 * @returns {undefined | NonNullable<NonNullable<import('@zcode/state-type').State['currentSearch']['listables']['nearby']>['popular']>['matching']}
 */
export const getNearbyMatching = (state) => {
  const hasMatches = getHasNearbyMatches(state)
  const hasPopular = getHasNearbyPopular(state)
  let key
  if (hasMatches) {
    key = 'matches'
  } else if (hasPopular) {
    key = 'popular'
  } else {
    return undefined
  }
  return state.currentSearch.listables.nearby?.[key]?.matching
}

/**
 * @param {import('@zcode/state-type').State} state
 */
export const getClustersByView = (state) => {
  const { searchUI } = state
  const searchView = searchUI ? searchUI.searchView : ActionC.SEARCH_VIEW_TYPE.MAIN

  switch (searchView) {
    case ActionC.SEARCH_VIEW_TYPE.SEARCHES: {
      return getSavedSearchesListables(state)
    }
    case ActionC.SEARCH_VIEW_TYPE.FAVORITES: {
      return getFavoritesListables(state)
    }
    case ActionC.SEARCH_VIEW_TYPE.MAIN:
    case ActionC.SEARCH_VIEW_TYPE.SAVE_SEARCH_CREATE: {
      return []
    }
    default:
      return null
  }
}

export const getSavedSearchesListables = createSelector(
  /**
   * @param {import('@zcode/state-type').State} state
   */
  (state) => state.savedSearchesView.listables.listables,
  (listables) => uniqueListables(listables)
)

export const getFavoritesListables = createSelector(
  /**
   * @param {import('@zcode/state-type').State} state
   */
  (state) => state.favoritesView.listables.listables,
  (listables) => uniqueListables(listables)
)

/**
 * @param {import('@zcode/state-type').State} state
 */
export const selectMinCitiesZoom = (state) => state.currentSearch.minCities.zoom

/**
 * @param {import('@zcode/state-type').State} state
 */
export function getCurrentLocation(state) {
  return state.currentSearch.location
}

export const selectCurrentCityStateSlug = compose(
  (/** @type {[string | undefined, string | undefined]} */ [cityName, stateName]) =>
    toSlug(cityName, stateName),
  createSelector(
    /**
     *
     * @param {import('@zcode/state-type').State} state
     * @param {import('react-router-dom').RouteComponentProps<{locationPath?: string}>)} routeProps
     * @returns {string | undefined}
     */
    (state, routeProps) => {
      return routeProps.match.params.locationPath
    },
    getCurrentCity,
    getCurrentHood,
    getCurrentLocation,
    (locationPath, city, hood, location) => {
      if (location.cityName) {
        return /** @type {[string, string]} */ ([location.cityName, location.stateName])
      }
      const { cityPath, hoodPath, statePath } = matchLocationPath(locationPath)
      const cityName = hood?.city || city?.name
      if (
        cityName &&
        (hoodPath || cityPath) &&
        statePath &&
        location.url === (hood?.path || city?.path).replace(/^\//, '')
      ) {
        return /** @type {[string, string | undefined]} */ ([cityName, hood?.state || city?.state])
      }
      return /** @type {[undefined, undefined]} */ ([undefined, undefined])
    }
  )
)

/**
 * @param {import('@zcode/state-type').State} state
 */
export function getCurrentLocationForSaveSearch(state) {
  return state.currentSearch && state.currentSearch.saveSearchLocation
}

/**
 * @param {import('@zcode/state-type').State} state
 */
export const getLocaleGuideLocale = (state) => {
  const { city, hood } = state.currentSearch
  const { cityName, hoodName } = getCurrentLocation(state)

  if (hood && hoodName && hood.name === hoodName) {
    return hood
  }
  if (city && cityName && city.name === cityName) {
    return city
  }

  return null
}

export const getLoadMoreCount = (/** @type {import('@zcode/state-type').State} */ state) => {
  return state.searchUI.loadMoreCount ?? 0
}

export const getHoveredClusterItem = (/** @type {import('@zcode/state-type').State} */ state) => {
  return state.searchUI.hoveredClusterItem
}

// returns current listables `matching` count
export const getMatchingCount = (/** @type {import('@zcode/state-type').State} */ state) => {
  const listables = getListablesRecordByView(state)
  const matching = listables ? (listables.matching ?? 0) : 0

  return matching
}

// returns current listables `listingCount` falling back to `matching` count
export const getListingCount = (/** @type {import('@zcode/state-type').State} */ state) => {
  const listables = getListablesRecordByView(state)
  return listables?.listingCount || listables?.matching || 0
}

export const getTotalMatchingCount = (/** @type {import('@zcode/state-type').State} */ state) => {
  // NOTE: The API combines the listables matching with nearby matching, meaning the
  // nearby matching is a total of listables and nearby listables.
  const { MAIN, SAVE_SEARCH_CREATE } = ActionC.SEARCH_VIEW_TYPE
  const searchView = getSearchView(state) || MAIN
  const hasNearbyListables = getHasNearbyListables(state)
  const isMainView = searchView === MAIN || searchView === SAVE_SEARCH_CREATE
  if (hasNearbyListables && isMainView) {
    const nearbyMatching = getNearbyMatching(state) || 0
    return nearbyMatching
  }

  return getMatchingCount(state)
}

/**
 * @param {import('@zcode/state-type').State} state
 */
export const checkBodyScrollDisabled = (state) => {
  return (
    isFiltersViewState(state) ||
    selectAnyModalOpen(state) ||
    selectIsFilterModalOpen(state) ||
    selectIsNavbarExpanded(state)
  )
}

/**
 * @param {import('@zcode/state-type').State} state
 */
export const getCheckVisited =
  (state) =>
  ({ uId, buildingId, listingId }) => {
    if (!uId && !buildingId && !listingId) {
      return false
    }
    const { visited } = state.bundle
    return (
      (uId && !!visited[uId]) ||
      (buildingId && !!visited[buildingId]) ||
      (listingId && !!visited[listingId])
    )
  }

/**
 * @param {import('@zcode/state-type').State} state
 */
export const getAllNearbyHoods = (state) => {
  return state.currentSearch.allNearbyHoods
}

/**
 *
 * @param {{ pathname: string }} location
 * @returns {`${'apartments' | 'condos' | 'houses' | 'rooms'}-for-rent`}
 */
export const getBasePath = (location) => {
  // TODO: revert once we split out routeDefinitions from routes and unit test stops breaking.
  // const match = matchRoutes(routeProps.location, mapRoutes)
  // const propertyTypePath = match && match.params && match.params.propertyTypePath

  // Temporary solution:
  const propertyTypePath = PROPERTY_TYPE_PATH_RE.exec(location.pathname)

  return propertyTypePath ? propertyTypePath[0] : routesConstants.APTS_BASE_URL
}

export const getPropertyTypeText = (routeProps) => {
  const basePath = getBasePath(routeProps.location)
  const propertyTypeDict = /** @type {const} */ ({
    [routesConstants.APTS_BASE_URL]: 'apartments',
    [routesConstants.CONDOS_BASE_URL]: 'condos',
    [routesConstants.HOUSES_BASE_URL]: 'houses',
    [routesConstants.ROOMS_BASE_URL]: 'rooms',
  })

  return propertyTypeDict[basePath] ?? 'rentals'
}

export const getPropertyTypeForRentText = (routeProps) => {
  const basePath = getBasePath(routeProps.location)
  let propertyTypeForRentText

  if (basePath === routesConstants.CONDOS_BASE_URL) {
    propertyTypeForRentText = 'cfr'
  } else if (basePath === routesConstants.HOUSES_BASE_URL) {
    propertyTypeForRentText = 'hfr'
  } else if (basePath === routesConstants.ROOMS_BASE_URL) {
    propertyTypeForRentText = 'rfr'
  } else {
    propertyTypeForRentText = 'afr'
  }

  return propertyTypeForRentText
}

/**
 * @param {import('@zcode/state-type').State} state
 */
export const getHoveredListable = (state) => {
  return state.searchUI.hoveredItem
}

/**
 * @param {import('@zcode/state-type').State} state
 */
export const selectIsListablesFetching = (state) => {
  return !!state.searchUI.isListablesFetching
}

export const selectIsPetFriendly = memoizeRouteProps((routeProps) => {
  const pathname = getPathname(routeProps)
  return pathname?.includes('pet-friendly')
})

export const selectIsCheap = memoizeRouteProps((routeProps) => {
  const pathname = getPathname(routeProps)
  return pathname?.includes('cheap')
})

export const selectIsLuxury = memoizeRouteProps((routeProps) => {
  const pathname = getPathname(routeProps)
  return pathname?.includes('luxury')
})

export const selectIsFurnished = memoizeRouteProps((routeProps) => {
  const pathname = getPathname(routeProps)
  return pathname?.includes('furnished')
})

export const selectIsShortTerm = memoizeRouteProps((routeProps) => {
  const pathname = getPathname(routeProps)
  return pathname?.includes('short-term')
})

export const selectIsLowIncome = memoizeRouteProps((routeProps) => {
  const pathname = getPathname(routeProps)
  return pathname?.includes('low-income')
})

export const selectIsNoSecurityDeposit = memoizeRouteProps((routeProps) => {
  const pathname = getPathname(routeProps)
  return pathname?.includes('no-security-deposit')
})

export const selectIsMoveInSpecial = memoizeRouteProps((routeProps) => {
  const pathname = getPathname(routeProps)
  return pathname?.includes('move-in-special')
})

export const selectIsUtilitiesIncluded = memoizeRouteProps((routeProps) => {
  const pathname = getPathname(routeProps)
  return pathname?.includes('utilities-included')
})

export const getSearchParams = memoizeRouteProps((routeProps) => {
  const entirePath = routeProps.location.pathname + routeProps.location.search
  const searchParams = [
    BEDROOMS_RE,
    BATHROOMS_RE,
    FILTERS_START_RE,
    LUXURY_RE,
    OVER_RE,
    PRICE_RANGE_RE,
    STUDIO_RE,
    UNDER_RE,
  ]

  return searchParams.some((s) => s.test(entirePath))
})

const BED_FACETS = ['/1-beds', '/2-beds', '/3-beds', '/4+beds', '/studios']

export const selectIsBeds = memoizeRouteProps((routeProps) => {
  const pathname = getPathname(routeProps)
  return BED_FACETS.some((facet) => pathname?.includes(facet))
})

export const selectIsVertical = memoizeRouteProps((routeProps) => {
  const isPetFriendly = selectIsPetFriendly(routeProps)
  const isCheap = selectIsCheap(routeProps)
  const isLowIncome = selectIsLowIncome(routeProps)
  const isLuxury = selectIsLuxury(routeProps)
  const isMoveInSpecial = selectIsMoveInSpecial(routeProps)
  const isFurnished = selectIsFurnished(routeProps)
  const isShortTerm = selectIsShortTerm(routeProps)
  const isNoSecurityDeposit = selectIsNoSecurityDeposit(routeProps)
  const isUtilitiesIncluded = selectIsUtilitiesIncluded(routeProps)

  return (
    isCheap ||
    isFurnished ||
    isLowIncome ||
    isLuxury ||
    isMoveInSpecial ||
    isNoSecurityDeposit ||
    isPetFriendly ||
    isShortTerm ||
    isUtilitiesIncluded
  )
})

/**
 * returns all vertical filter names found in URL separated by space
 */
export const getVerticalTypes = memoizeRouteProps((routeProps) => {
  if (typeof routeProps?.location?.pathname === 'string') {
    return routeProps.location.pathname
      .split('/')
      .filter((part) => `/${part}`.match(FILTERS_START_RE))
      .join(' ')
      .replace(/-/g, ' ')
      .replace(/studios/g, 'studio')
  }
  return ''
})

/**
 * returns array of vertical filter names found in URL
 *
 * @deprecated `import { getVerticalsArray } from '@zcode/nr-utils'` instead
 */
export const getVerticalsArray = memoizeRouteProps((routeProps) => {
  if (routeProps.location) {
    return _getVerticalsArray(routeProps.location)
  }
  return []
})

export const getCurrentCityPath = createSelector(getCurrentCity, (currentCity) => {
  return currentCity?.path
})

export const selectIsApartmentBuildingsPage = (routeProps) => {
  const { location } = routeProps

  if (!location) {
    return false
  }

  const { pathname } = location

  return pathname && pathname.startsWith(`/${routesConstants.BLDG_BASE_URL}`)
}

export const selectRouteLocation = (routeProps) => {
  return routeProps && routeProps.location
}
export const selectRouteHash = (routeProps) => {
  const location = selectRouteLocation(routeProps)
  const hash = location && location.hash
  return hash && hash.substring(1)
}
export const selectHashQuery = (routeProps) => {
  const hash = selectRouteHash(routeProps)
  return hash && qs.parse(hash)
}
export const selectBackUrl = (routeProps) => {
  const hashQuery = selectHashQuery(routeProps)
  return hashQuery && hashQuery.back
}
